import * as React from 'react';
import { $getListDepth, $isListItemNode, $isListNode } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $findMatchingParent } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  INDENT_CONTENT_COMMAND,
  KEY_TAB_COMMAND,
  OUTDENT_CONTENT_COMMAND,
} from 'lexical';

interface ListTabIndentationPluginProps {
  disabled?: boolean;
}

export function ListTabIndentationPlugin({
  disabled = false,
}: ListTabIndentationPluginProps) {
  const [editor] = useLexicalComposerContext();

  React.useEffect(() => {
    if (disabled) {
      return;
    }

    return editor.registerCommand(
      KEY_TAB_COMMAND,
      (event) => {
        let shouldHandleTab = false;
        let shouldIndent = false;
        let shouldOutdent = false;

        editor.getEditorState().read(() => {
          const selection = $getSelection();

          if (!$isRangeSelection(selection)) {
            return;
          }

          const anchorNode = selection.anchor.getNode();
          const listItemNode = $findMatchingParent(anchorNode, $isListItemNode);
          const parentListNode =
            listItemNode && $findMatchingParent(listItemNode, $isListNode);

          if (!listItemNode || !parentListNode) {
            return;
          }

          event.preventDefault();
          shouldHandleTab = true;

          if (event.shiftKey) {
            shouldOutdent = true;
            return;
          }

          shouldIndent = $getListDepth(parentListNode) < 2;
        });

        if (!shouldHandleTab) {
          return false;
        }

        if (shouldOutdent) {
          editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
        } else if (shouldIndent) {
          editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
        }

        return true;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [disabled, editor]);

  return null;
}
